import { useState, useEffect, useCallback } from 'react';
import { Search, FileText, Link2, Loader2, Files } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EmptyState } from '@shared/components/EmptyState';
import { projectAPI } from '@/utils/supabase/client';
import { DocumentSummary, DocumentStatus, DOCUMENT_TYPE_LABELS } from '@/types/documents';
import { toast } from 'sonner';

const STATUS_OPTIONS: { label: string; value: DocumentStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Draft', value: 'draft' },
  { label: 'Published', value: 'published' },
  { label: 'Archived', value: 'archived' },
];

export function DocumentsIndexPage() {
  const [docs, setDocs] = useState<DocumentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<DocumentStatus | 'all'>('all');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await projectAPI.getDocuments(status === 'all' ? undefined : { status });
      setDocs(list);
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const copyShareLink = async (doc: DocumentSummary) => {
    setBusyId(doc.id);
    try {
      const detail = await projectAPI.getDocument(doc.id);
      const active = detail.shares.find((s) => !s.revoked);
      const share = active || (await projectAPI.createDocumentShare(doc.id));
      await navigator.clipboard.writeText(`${window.location.origin}/p/${share.public_token}`);
      toast.success('Share link copied to clipboard');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create share link');
    } finally {
      setBusyId(null);
    }
  };

  const filtered = docs.filter((d) => d.title.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div className="flex flex-1 flex-col gap-6 overflow-auto p-4 lg:p-6">
      <div>
        <h1 className="flex items-center gap-2 font-serif text-2xl font-semibold tracking-tight">
          <Files className="h-6 w-6" />
          Documents
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Everything shared with prospects and clients, in one place.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-48 flex-1 max-w-80">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documents…"
            className="pl-9"
          />
        </div>
        <Select value={status} onValueChange={(v) => setStatus(v as DocumentStatus | 'all')}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No documents yet"
          description="Create documents from a prospect or client record to share them here."
        />
      ) : (
        <div className="space-y-2">
          {filtered.map((doc) => {
            const busy = busyId === doc.id;
            return (
              <div
                key={doc.id}
                className="flex flex-col gap-2 rounded-md border border-border p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate font-medium">{doc.title}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline" className="text-xs">{DOCUMENT_TYPE_LABELS[doc.type]}</Badge>
                    <Badge variant={doc.status === 'published' ? 'success' : 'secondary'} className="text-xs">
                      {doc.status}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      Updated {new Date(doc.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyShareLink(doc)}
                  disabled={busy || doc.status !== 'published'}
                  title={doc.status !== 'published' ? 'Publish first to share' : 'Copy public link'}
                  className="shrink-0"
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
                  <span className="ml-1">Copy link</span>
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
